import moment from "moment";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import SvgIcon from "../../../../parts/SvgIcon";
import { BASELINE_MIN_CHECK_INS } from "../../../../utils/readiness-report";
import {
  formatUtcDay,
  sectionTranslator,
  useIntensity,
} from "./readinessFormat";

const MAX_MISSED_DATES_SHOWN = 6;

const TONE = {
  info: {
    icon: "/img/new-icons/w-info-circle.svg",
    mod: "rr-insights__row--info",
  },
  warning: {
    icon: "/img/new-icons/w-exclamation-circle.svg",
    mod: "rr-insights__row--warn",
  },
};

const tr = sectionTranslator("insights");

const formatDay = (utcDate) => formatUtcDay(utcDate, "MMM D");

const isNextDay = (previous, utcDate) =>
  moment(String(previous), "YYYYMMDD")
    .add(1, "day")
    .isSame(moment(String(utcDate), "YYYYMMDD"), "day");

// Runs of consecutive days become one range ("Apr 26-Apr 30"); a lone day stays as it is
const groupConsecutive = (days) =>
  days.reduce((runs, utcDate) => {
    const run = runs[runs.length - 1];
    if (run && isNextDay(run[run.length - 1], utcDate)) run.push(utcDate);
    else runs.push([utcDate]);
    return runs;
  }, []);

/**
 * Formats the missed dates text.
 * @param {*} t
 * @param {*} missed
 * @returns
 */
const missedDatesText = (t, missed) => {
  const shown = groupConsecutive(missed.slice(0, MAX_MISSED_DATES_SHOWN))
    .map((run) =>
      run.length === 1
        ? formatDay(run[0])
        : `${formatDay(run[0])}-${formatDay(run[run.length - 1])}`
    )
    .join(", ");
  const hidden = missed.length - MAX_MISSED_DATES_SHOWN;
  return hidden > 0
    ? tr(t, "missedDaysMore", { dates: shown, count: hidden })
    : shown;
};

// The sentence of one insight (see buildReadinessReport): `type` + `params` in, translated text out
export const insightText = (t, insight, athleteName, intensity = {}) => {
  const { type, params } = insight;
  const { context, toMeasure = (value) => value } = intensity;
  switch (type) {
    case "learning":
      return athleteName
        ? tr(t, "learning", {
            ...params,
            name: athleteName,
            min: BASELINE_MIN_CHECK_INS,
          })
        : tr(t, "learningSelf", { ...params, min: BASELINE_MIN_CHECK_INS });
    case "belowNormal":
      // The latest day can have no normal yet (a long gap before it): leave the range out, not blank
      return tr(t, params.lo == null ? "belowNormalNoRange" : "belowNormal", {
        ...params,
        count: params.judged,
      });
    case "questionDrove":
      return tr(t, "questionDrove", {
        ...params,
        question: t(`readinessRate.${params.question}`),
      });
    case "missedDays":
      return tr(t, "missedDays", {
        ...params,
        dates: missedDatesText(t, params.missed),
      });
    case "rpeOnBelowDays":
      return tr(t, "rpeOnBelowDays", {
        avgBelow: toMeasure(params.avgBelow),
        avgOther: toMeasure(params.avgOther),
        context,
      });
    case "latestLowest":
      return tr(t, "latestLowest", {
        ...params,
        date: formatDay(params.utcDate),
      });
    default:
      return null;
  }
};

/**
 * Renders the insights for the readiness report.
 * @param {*} insights
 * @param {*} athleteName
 * @returns
 */
export const ReadinessInsights = ({ insights, athleteName }) => {
  const { t } = useTranslation("athlete-portal");
  const intensity = useIntensity();
  const rows = insights
    .map((insight) => ({
      key: insight.type,
      tone: TONE[insight.tone] ?? TONE.info,
      text: insightText(t, insight, athleteName, intensity),
    }))
    .filter((row) => row.text);
  if (rows.length === 0) return null;

  return (
    <div className="rr-insights">
      <div className="rr-insights__header">
        <SvgIcon
          src="/img/new-icons/w-info-circle.svg"
          useCurrentColor
          classNames="rr-insights__header-icon"
        />
        <span className="rr-insights__title">{tr(t, "title")}</span>
        <span className="rr-insights__subtitle">{tr(t, "subtitle")}</span>
      </div>
      <div className="rr-insights__rows">
        {rows.map((row) => (
          <div key={row.key} className={`rr-insights__row ${row.tone.mod}`}>
            <SvgIcon
              src={row.tone.icon}
              useCurrentColor
              classNames="rr-insights__row-icon"
            />
            <p className="rr-insights__row-text">{row.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

ReadinessInsights.propTypes = {
  insights: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      tone: PropTypes.string,
      params: PropTypes.object,
    })
  ).isRequired,
  athleteName: PropTypes.string,
};
